import { create } from 'zustand';
import type { LoginResponseData, PublicUser } from '@cosvision/shared';
import { registerTokenAccessor, request } from '../lib/api.js';
import {
  clearStoredRefreshToken,
  nativeRefreshBody,
  setStoredRefreshToken,
} from '../lib/native-refresh-store.js';
import { removePushRegistration } from '../lib/push-notifications.js';

interface AuthState {
  user: PublicUser | null;
  accessToken: string | null;
  status: 'idle' | 'authenticating' | 'authenticated' | 'unauthenticated';
  error: string | null;

  login: (username: string, password: string) => Promise<void>;
  /**
   * Re-authenticate in place for the native app-lock screen. Unlike
   * `login`, a failure does NOT clear the session state — the user stays
   * locked and can retry. Throws on bad password so the caller can render
   * the error.
   */
  unlockWithPassword: (password: string) => Promise<void>;
  logout: () => Promise<void>;
  hydrateFromRefresh: () => Promise<boolean>;
  /** Replace the cached user (e.g. after a profile update). */
  setUser: (user: PublicUser) => void;

  // Internal — used by the axios interceptor.
  _setAccessToken: (token: string | null) => void;
  _setUnauthenticated: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  accessToken: null,
  status: 'idle',
  error: null,

  async login(username, password) {
    set({ status: 'authenticating', error: null });
    try {
      const data = await request<LoginResponseData>('post', '/auth/login', {
        username,
        password,
      });
      // Native only: the backend returns the refresh token in the body for
      // the Android app; park it in the Keystore. On web the field is absent
      // (the HTTP-only cookie carries it) and this is a no-op.
      if (data.refreshToken) {
        await setStoredRefreshToken(data.refreshToken);
      }
      set({
        user: data.user,
        accessToken: data.accessToken,
        status: 'authenticated',
        error: null,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed';
      set({ user: null, accessToken: null, status: 'unauthenticated', error: message });
      throw err;
    }
  },

  async unlockWithPassword(password) {
    const current = get().user;
    if (!current) throw new Error('No active session to unlock');
    const data = await request<LoginResponseData>('post', '/auth/login', {
      username: current.username,
      password,
    });
    if (data.refreshToken) {
      await setStoredRefreshToken(data.refreshToken);
    }
    set({ user: data.user, accessToken: data.accessToken, status: 'authenticated', error: null });
  },

  async logout() {
    // Drop this device's push token first — the DELETE needs the still-valid
    // access token (native only; no-op on web).
    await removePushRegistration();
    try {
      // Native sends the Keystore-held token so the server can audit the
      // logout; web sends no body (the cookie identifies the session).
      await request<{ message: string }>('post', '/auth/logout', await nativeRefreshBody());
    } catch {
      // Best-effort — clear local state regardless.
    }
    await clearStoredRefreshToken();
    set({ user: null, accessToken: null, status: 'unauthenticated', error: null });
  },

  /**
   * Called once at app startup. Attempts to mint an access token from the
   * httpOnly refresh cookie (web) or the Keystore-held refresh token
   * (native); if it works, hydrate the user via /auth/me. Returns true when
   * the user is authenticated after the call.
   */
  async hydrateFromRefresh() {
    set({ status: 'authenticating', error: null });
    try {
      const { accessToken } = await request<{ accessToken: string; expiresIn: number }>(
        'post',
        '/auth/refresh',
        await nativeRefreshBody(),
      );
      set({ accessToken });
      const me = await request<PublicUser>('get', '/auth/me');
      set({ user: me, status: 'authenticated' });
      return true;
    } catch {
      set({ user: null, accessToken: null, status: 'unauthenticated' });
      return false;
    }
  },

  setUser(user) {
    set({ user });
  },

  _setAccessToken(token) {
    set({ accessToken: token });
  },

  _setUnauthenticated() {
    set({ user: null, accessToken: null, status: 'unauthenticated' });
  },
}));

// Wire the axios interceptor to this store. Done once at module load.
registerTokenAccessor({
  getAccessToken: () => useAuthStore.getState().accessToken,
  setAccessToken: (token) => useAuthStore.getState()._setAccessToken(token),
  onUnauthenticated: () => useAuthStore.getState()._setUnauthenticated(),
});
